<script setup lang="ts">
  import { computed } from 'vue';
  import type { TrainingDetail } from '@/entities/Training';
  import { formatTrainingControlValue } from '../../lib/formatTrainingControlValue';

  /**
   * The two screens read the same data under different specs. `CONTROL` is the printed quality
   * record, which always reserves a carga horária box and lays the fields out in equal thirds.
   * `VIEW` is the Dados Treinamento tab, which follows the prototype column rhythm and answers
   * Sim/Não even for a record with no certificate data — there the section heading carries the
   * label, so the inner one would only repeat it. Carga horária is collected for every Treinamento
   * Aberto regardless of "Necessário certificado?", so both screens always show it; only the
   * certificate type stays conditional, since it genuinely doesn't exist without a certificate.
   */
  const props = withDefaults(
    defineProps<{
      certificate: TrainingDetail['certificate'];
      variant?: 'CONTROL' | 'VIEW';
    }>(),
    { variant: 'CONTROL' }
  );

  const isViewVariant = computed(() => props.variant === 'VIEW');

  const fieldsClass = computed(() =>
    isViewVariant.value ? 'flex flex-wrap gap-4' : 'grid gap-4 md:grid-cols-3'
  );

  const requiredLabel = computed(() => {
    if (isViewVariant.value) return props.certificate?.required === true ? 'Sim' : 'Não';
    if (!props.certificate) return formatTrainingControlValue(null);
    return props.certificate.required ? 'Sim' : 'Não';
  });

  const showType = computed(() => props.certificate?.required === true);

  const workloadHoursLabel = computed(() => {
    const hours = props.certificate?.workloadHours;
    return hours !== null && hours !== undefined ? `${hours} horas` : formatTrainingControlValue(null);
  });
</script>

<template>
  <div :class="fieldsClass">
    <div
      class="flex flex-col gap-2"
      :class="isViewVariant && 'w-full md:w-37'"
    >
      <span
        v-if="!isViewVariant"
        class="text-xs font-semibold text-gray-07"
      >
        Necessário certificado?
      </span>
      <span
        data-testid="view-training-certificate-required"
        class="rounded-2xl border border-border-color bg-gray-02/20 px-5 py-3 text-sm text-gray-07"
      >
        {{ requiredLabel }}
      </span>
    </div>

    <div
      v-if="showType"
      class="flex flex-col gap-2"
      :class="isViewVariant && 'w-full md:w-67'"
    >
      <span class="text-xs font-semibold text-gray-07">Tipo de certificado:</span>
      <span
        data-testid="view-training-certificate-type"
        class="rounded-2xl border border-border-color bg-gray-02/20 px-5 py-3 text-sm text-gray-07"
      >
        {{ formatTrainingControlValue(certificate?.type) }}
      </span>
    </div>

    <div
      class="flex flex-col gap-2"
      :class="isViewVariant && 'w-full md:w-45'"
    >
      <span class="text-xs font-semibold text-gray-07">Carga horária:</span>
      <span
        data-testid="view-training-certificate-workload"
        class="rounded-2xl border border-border-color bg-gray-02/20 px-5 py-3 text-sm text-gray-07"
      >
        {{ workloadHoursLabel }}
      </span>
    </div>
  </div>
</template>
